<template>
  <div>
    <div class="coin-info">
      <div class="input-search pr" @click="isShowSearchBox(true)">
        <input
          type="text"
          :placeholder="$t('trading.search.searchCoin')"
          v-model="searchStr"
          @input="searchCoinList"
        />
        <img
          class="ab img"
          src="@/assets/images/common/icon_select_arrow.svg"
          alt="arrow-down"
        />
      </div>
      <div class="too-price">
        <p :class="['price', priceColor]">{{ price }}</p>
        <div>
          <p>{{ $t("trading.search.24high") }}</p>
          <span class="green">{{
            formatNumberWithComma(symbolPriceInfo?.high)
          }}</span>
        </div>
        <div>
          <p>{{ $t("trading.search.24low") }}</p>
          <span class="red">{{
            formatNumberWithComma(symbolPriceInfo?.low)
          }}</span>
        </div>
        <div>
          <p>{{ $t("trading.search.24vol", { ticker }) }}</p>
          <span>{{ formatNumberWithComma(symbolPriceInfo?.volume) }} </span>
        </div>
      </div>

      <div class="coin-list-box" v-if="isShowSearch">
        <ul class="coin-list">
          <li
            v-for="item in coinList"
            :key="item"
            @click="changeSelectCoin(item)"
          >
            <img class="coin-img" :src="getCoinIcon(item)" />
            <p>{{ item }}</p>
          </li>
        </ul>
        <button class="close-btn" @click="isShowSearchBox(false)">
          {{ $t("trading.common.close") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { throttle } from "lodash-es";
import { formatNumberWithComma } from "@/utils/order";
import { onMounted, onUnmounted, ref, watch, computed } from "vue";
import { useStore } from "vuex";
const store = useStore();
let timer;
const socket = ref(null);
const socket2 = ref(null);
const previousPrice = ref(null);
const price = ref("");
const priceColor = ref("black");
const ticker = ref("BTC");
const isShowSearch = ref(false);
const searchStr = ref("");
const storeTicker = computed(() => store.state.transact.ticker);
const coinList = computed(() => {
  const list = store.state.transact.coinList;
  if (searchStr.value !== "") {
    const filterd = list.filter((v) => v.includes(searchStr.value));
    return filterd;
  } else {
    return list;
  }
});
const countryCode = computed(() => store.state.transact.countryCode);
const symbolPriceInfo = computed(() => store.state.transact.symbolPriceInfo);
const isBitget = computed(() => store.state.transact.isBitget);
const getCoinIcon = (coin) => {
  const symbol = coin.slice(0, -4);
  try {
    return require(`@/assets/images/coin_logos/${symbol.toLowerCase()}.png`);
  } catch {
    return require("@/assets/images/coin_logos/default_dark.png");
  }
};

const searchCoinList = () => {
  searchStr.value = searchStr.value.toUpperCase();
};
const changeSelectCoin = (coin) => {
  const symbol = coin.slice(0, -4);
  ticker.value = symbol.toUpperCase();
  store.commit("transact/setTicker", ticker.value);
  searchStr.value = ticker.value;
  isShowSearch.value = false;
};
const isShowSearchBox = (bool) => {
  isShowSearch.value = bool;
  if (bool) {
    searchStr.value = "";
  }
};
const updatePriceThrottled = throttle((newPrice) => {
  if (previousPrice.value !== null) {
    if (newPrice > previousPrice.value) {
      priceColor.value = "green";
    } else if (newPrice < previousPrice.value) {
      priceColor.value = "red";
    } else {
      priceColor.value = priceColor.value === "red" ? "red" : "green";
    }
  }

  previousPrice.value = newPrice;
  price.value = newPrice;
  store.commit("transact/setLastPrice", price.value);
}, 500);

const connectSocket = (pair) => {
  if (socket.value) socket.value.close();
  let url;
  let message;
  if (isBitget.value) {
    url = "wss://ws.bitget.com/v2/ws/public";

    message = JSON.stringify({
      op: "subscribe",
      args: [
        {
          instType: "USDT-FUTURES",
          channel: "ticker",
          instId: pair, // ex. "BTCUSDT"
        },
      ],
    });
  } else {
    url = `wss://fstream.binance.com/ws/${pair.toLowerCase()}@trade`;
    message = null;
  }
  socket.value = new WebSocket(url);
  socket.value.onopen = () => {
    if (message) {
      socket.value.send(message); // Bitget에 심볼 구독 요청
    }
  };
  socket.value.onmessage = (event) => {
    let data;

    try {
      data = JSON.parse(event.data);
    } catch (error) {
      console.error("Error parsing WebSocket data:", error);
      return;
    }
    let newPrice;
    // Bitget에서 가격 추출
    if (isBitget.value) {
      // Bitget의 응답 구조에 맞춰 가격 추출
      if (
        !data ||
        data.event === "subscribe" ||
        !Array.isArray(data.data) ||
        data.data.length === 0
      ) {
        return;
      }
      // const d = data?.data ? data?.data[0] : [{lastPr:0}];
      const d = data?.data[0];
      if (!d || !d.lastPr) return;
      newPrice = parseFloat(d.lastPr);
      if (isNaN(newPrice)) return;
    } else {
      newPrice = parseFloat(data.p);
    }

    // 가격 색상 변경 (상승/하락/유지)
    if (!isNaN(newPrice)) {
      updatePriceThrottled(newPrice);
    } else {
      console.error("Invalid price data received:", data);
    }
  };
};

const fetchDepthSnapshot = async (pair) => {
  const res = await fetch(
    `https://fapi.binance.com/fapi/v1/depth?symbol=${pair}&limit=5`,
  );
  const data = await res.json();
  return {
    bidPrice: parseFloat(data?.bids?.[0]?.[0] ?? 0),
    askPrice: parseFloat(data?.asks?.[0]?.[0] ?? 0),
  };
};

const connectSocketDepth = async (pair) => {
  // pair 형태 BTCUSDT
  if (socket2.value) socket2.value.close();
  let url;
  let message;
  if (isBitget.value) {
    url = "wss://ws.bitget.com/v2/ws/public";

    message = JSON.stringify({
      op: "subscribe",
      args: [
        {
          instType: "USDT-FUTURES",
          channel: "books5",
          instId: pair, // ex. "BTCUSDT"
        },
      ],
    });
  } else {
    url = `wss://fstream.binance.com/ws/${pair.toLowerCase()}@depth20@100ms`;
    message = null;

    const snapshot = await fetchDepthSnapshot(pair);
    store.commit("transact/setDepthPrice", snapshot);
  }

  socket2.value = new WebSocket(url);

  socket2.value.onopen = () => {
    if (message) {
      socket2.value.send(message);
    }
  };

  socket2.value.onmessage = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      return;
    }
    let bidPrice, askPrice;
    let asks = [],
      bids = [];

    // Bitget

    if (isBitget.value) {
      if (
        data.event === "subscribe" ||
        !Array.isArray(data.data) ||
        data.data.length === 0
      ) {
        return;
      }

      const d = data.data[0];
      asks = d.asks.map(([price, amount]) => ({
        price: parseFloat(price),
        amount: parseFloat(amount),
      }));
      bids = d.bids.map(([price, amount]) => ({
        price: parseFloat(price),
        amount: parseFloat(amount),
      }));
      bidPrice = parseFloat(d.bids[0][0]); // 가장 높은 매수 호가
      askPrice = parseFloat(d.asks[0][0]); // 가장 낮은 매도 호가
      if (isNaN(bidPrice) || isNaN(askPrice)) return;
    } else {
      const depthData = data;
      asks = (
        depthData.a?.map(([price, amount]) => ({
          price: parseFloat(price),
          amount: parseFloat(amount),
        })) || []
      ).slice(0, 12); // 최대 7개만 선택
      bids = (
        depthData.b?.map(([price, amount]) => ({
          price: parseFloat(price),
          amount: parseFloat(amount),
        })) || []
      ).slice(0, 12); // 최대 7개만 선택
      bidPrice = parseFloat(depthData?.b[0][0]); // 매수 가격
      askPrice = parseFloat(depthData?.a[0][0]); // 매도 가격
    }

    store.commit("transact/setDepthPrice", { bidPrice, askPrice });
    store.commit("transact/setOrderbookInfo", { asks, bids });
  };
};

watch(
  () => storeTicker.value,
  async (newTicker) => {
    if (newTicker) {
      ticker.value = newTicker;
    }

    const symbol = `${storeTicker.value}USDT`;
    searchStr.value = storeTicker.value;
    connectSocket(symbol);
    connectSocketDepth(symbol);
    await store.dispatch("transact/getSymbolPrice");
  },
);
watch(
  () => countryCode.value,
  async () => {
    const symbol = `${ticker.value}USDT`;
    connectSocket(symbol);
    connectSocketDepth(symbol);
    await store.dispatch("transact/getSymbolPrice");
  },
);

onMounted(async () => {
  await store.dispatch("transact/fetchCoinList");
  await store.dispatch("transact/getSymbolPrice");
  timer = setInterval(() => {
    store.dispatch("transact/getSymbolPrice");
  }, 600000);
});
onUnmounted(() => {
  if (socket.value) socket.value.close();
  if (socket2.value) socket2.value.close();
  clearInterval(timer);
});
</script>

<style lang="scss" scoped>
@use "@/assets/scss/components/trading/coinSearchView.scss" as coins;
</style>
